/**
 * NarrativeBlock — the AI-written board summary as a Carbon tile: the
 * section heading, Guardian's one-line framing (composed deterministically
 * by the brief composer, not the AI), the narrative in a reading column, and
 * a provenance line led by the iuvo mark.
 *
 * Empty handling: when the narrative is absent (the AI call failed or is not
 * configured), the tile still renders and says so, so the reader never
 * wonders whether the section was meant to appear.
 */

import clsx from 'clsx'
import { IuvoMark, Tile } from '@/components/carbon'

interface Props {
  narrative: string | null
  /** One line above the narrative, composed by the brief composer (narrativeDeck). */
  deck: string
  /** Tenant name for the provenance line ("Composed by Guardian for {tenantName}"). */
  tenantName: string
  /** Reporting window in days, named in the provenance line. */
  periodDays: number
  className?: string
}

export default function NarrativeBlock({ narrative, deck, tenantName, periodDays, className }: Props) {
  const hasNarrative = typeof narrative === 'string' && narrative.trim().length > 0

  return (
    <Tile as="section" aria-labelledby="exec-narrative-heading" className={clsx('flex min-w-0 flex-col pb-6', className)}>
      <h2 id="exec-narrative-heading" className="m-0 text-heading font-normal text-text-primary">
        Board summary
      </h2>
      <p className="m-0 mt-0.5 text-helper text-text-helper">{deck}</p>

      {hasNarrative ? (
        <p className="m-0 mt-4 max-w-[68ch] whitespace-pre-wrap border-t border-line-subtle pt-4 text-body-lg text-text-primary">
          {narrative}
        </p>
      ) : (
        <p className="m-0 mt-4 border-t border-line-subtle py-6 text-helper text-text-helper">
          Guardian could not write a summary for this period. The figures above stand on their own; the summary returns
          with the next report.
        </p>
      )}

      <p className="m-0 mt-auto flex items-center gap-2 pt-5 text-helper text-text-helper">
        <IuvoMark size={14} />
        <span className="min-w-0">
          Composed by Guardian for {tenantName} · {periodDays}-day period
        </span>
      </p>
    </Tile>
  )
}
